/**
 * useAgentUpdates - Hook for real-time AI agent status updates
 * Uses WebSocket with polling fallback for real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ClaimPipeline } from '../types';
import { api, getAuthToken } from '../../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 10000; // 10 seconds (fallback if WebSocket fails)
const WS_RECONNECT_DELAY = 3000; // 3 seconds

interface UseAgentUpdatesOptions {
  pollInterval?: number;
  enableWebSocket?: boolean;
}

interface UseAgentUpdatesResult {
  pipeline: ClaimPipeline | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  connected: boolean;
}

export function useAgentUpdates(
  claimId?: string,
  options: UseAgentUpdatesOptions = {}
): UseAgentUpdatesResult {
  const { pollInterval = POLL_INTERVAL, enableWebSocket = true } = options;

  const [pipeline, setPipeline] = useState<ClaimPipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Bumped on every connect attempt and on cleanup/claimId-change; an async mint
  // round-trip captures its value and bails if it's been superseded, so a socket
  // opened after unmount (or after the claim changed) can't become a zombie that
  // reconnects forever.
  const connGenRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);

  // Fetch pipeline status via REST API (with auth)
  const fetchPipelineStatus = useCallback(async () => {
    try {
      const endpoint = claimId
        ? `/api/v1/pipeline/status/${claimId}`
        : `/api/v1/pipeline/status`;

      const data = await api.get<ClaimPipeline>(endpoint, { showErrorToast: false });
      setPipeline(data);
      setError(null);
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch pipeline status';
      console.warn('Pipeline status fetch failed:', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchPipelineStatus();
  }, [fetchPipelineStatus]);

  // WebSocket connection
  const connectWebSocket = useCallback(async () => {
    if (!enableWebSocket || !BACKEND_URL) return;

    // This attempt's generation. If it's superseded (unmount, claimId change, or a
    // newer connect) while we await the token mint, we bail instead of opening a
    // socket that would outlive the component and reconnect/re-mint forever.
    const myGen = ++connGenRef.current;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // AUTHZ (2026-10-03): the pipeline WS now requires a short-lived token minted
    // by an authenticated REST call (the org gate runs there), then passed as
    // ?token=. Mint it first (re-minted on every reconnect since this runs fresh).
    // If we can't mint (signed out / network), don't open an unauthenticated
    // socket — it would receive no data — fall back to REST polling instead.
    const reportId = claimId || 'all';
    let wsToken = '';
    try {
      const authToken = await getAuthToken();
      const resp = await fetch(
        `${BACKEND_URL}/api/v1/ws/mint-token?report_id=${encodeURIComponent(reportId)}`,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
      );
      if (resp.ok) {
        wsToken = (await resp.json())?.token || '';
      }
    } catch (err) {
      console.warn('Failed to mint pipeline WS token:', err);
    }

    // Bail if this attempt was superseded while awaiting the mint.
    if (myGen !== connGenRef.current) return;

    if (!wsToken) {
      setConnected(false);
      // Poll as the immediate fallback, and retry the socket with capped backoff
      // so a transient mint failure (e.g. during a backend redeploy) recovers
      // instead of demoting the session to polling until the next remount — while
      // a signed-out tab backs off rather than hammering the mint endpoint.
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
      }
      const attempt = reconnectAttemptsRef.current++;
      const delay = Math.min(30000, WS_RECONNECT_DELAY * Math.pow(2, attempt));
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (myGen === connGenRef.current) connectWebSocket();
      }, delay);
      return;
    }

    try {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const base = claimId
        ? `${wsUrl}/api/v1/ws/pipeline/${claimId}`
        : `${wsUrl}/api/v1/ws/pipeline`;
      const wsEndpoint = `${base}?token=${encodeURIComponent(wsToken)}`;

      console.log('Connecting to pipeline WebSocket:', base);
      wsRef.current = new WebSocket(wsEndpoint);

      wsRef.current.onopen = () => {
        console.log('✅ Pipeline WebSocket connected');
        setConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;

        // Stop polling when WebSocket is connected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'initial_status' || data.type === 'refresh') {
            if (data.pipeline) {
              setPipeline(data.pipeline);
              setLoading(false);
            }
          } else if (data.type?.startsWith('agent_')) {
            // Update specific agent in pipeline
            setPipeline(prev => {
              if (!prev) return prev;

              const updatedAgents = prev.agents.map(agent => {
                if (agent.agentId === data.agentId) {
                  return {
                    ...agent,
                    status: data.type === 'agent_started' ? 'running' :
                            data.type === 'agent_complete' ? 'complete' :
                            data.type === 'agent_error' ? 'error' :
                            data.type === 'agent_progress' ? 'running' :
                            agent.status,
                    progress: data.current && data.total ? {
                      current: data.current,
                      total: data.total,
                      percentage: data.percentage || Math.round((data.current / data.total) * 100)
                    } : agent.progress,
                    result: data.summary ? { summary: data.summary } : agent.result
                  };
                }
                return agent;
              });

              return {
                ...prev,
                agents: updatedAgents,
                currentAgent: data.type === 'agent_started' ? data.agentId :
                              data.type === 'agent_complete' ? undefined :
                              prev.currentAgent,
                lastUpdated: new Date().toISOString()
              };
            });
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', err);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);

        // Don't reconnect if this socket's generation was superseded (unmount /
        // claimId change) — prevents a post-unmount reconnect loop.
        if (myGen !== connGenRef.current) return;

        // Reconnect after delay
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (myGen === connGenRef.current) connectWebSocket();
        }, WS_RECONNECT_DELAY);

        // Start polling as fallback
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
        }
      };

      wsRef.current.onerror = (err) => {
        console.warn('WebSocket error:', err);
        setConnected(false);
      };

    } catch (err) {
      console.warn('WebSocket connection failed:', err);
      setConnected(false);

      // Fall back to polling
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
      }
    }
  }, [claimId, enableWebSocket, fetchPipelineStatus, pollInterval]);

  // Initial fetch and connection setup
  useEffect(() => {
    fetchPipelineStatus();

    if (enableWebSocket) {
      connectWebSocket();
    } else {
      // Use polling if WebSocket disabled
      pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
    }

    return () => {
      // Invalidate any in-flight connect (awaiting a mint) and any pending
      // reconnect so nothing opens or re-mints after unmount / claimId change.
      connGenRef.current++;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchPipelineStatus, connectWebSocket, enableWebSocket, pollInterval]);

  return { pipeline, loading, error, refresh, connected };
}

export default useAgentUpdates;
